import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, CheckCheck, X, Truck, MapPinCheck, Wrench, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "@/lib/api";
import { techSend, patchCache } from "@/pages/tech/offline";

const NEW_STATUS = { accept: "accepted", start_journey: "on_the_way", arrived: "arrived", start_repair: "in_progress", complete: "completed" };

const NEXT = {
  assigned: [["accept", "Accept Job", CheckCheck]], accepted: [["start_journey", "Start Journey", Truck]], on_the_way: [["arrived", "Mark Arrived", MapPinCheck]],
  arrived: [["start_repair", "Start Repair", Wrench]], in_progress: [["complete", "Complete Repair", BadgeCheck]],
};
const ETAS = [15, 30, 45, 60, 90];

export const JobActions = ({ job, onRejected }) => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const [eta, setEta] = useState(30);
  const [rej, setRej] = useState(false);
  const [reason, setReason] = useState("");
  const run = async (action, extra = {}) => {
    if (action === "complete" && !window.confirm("Mark this repair as completed?")) return;
    setBusy(action);
    try {
      const res = await techSend("post", `/tech/jobs/${job.id}/action`, { action, ...extra }, `${action.replace(/_/g, " ")} · ${job.booking_no}`);
      if (res.queued) {
        const upd = (j) => (action === "reject" ? j : { ...j, status: NEW_STATUS[action], timeline: [...(j.timeline || []), { status: NEW_STATUS[action], at: new Date().toISOString(), note: "Saved offline" }] });
        qc.setQueryData(["tech", "job", job.id], (j) => j && upd(j));
        patchCache(`/tech/jobs/${job.id}`, upd);
        toast.message("Saved offline — will sync when internet is back");
      } else {
        toast.success(action === "reject" ? "Job rejected — admin has been notified" : "Status updated");
        qc.invalidateQueries({ queryKey: ["tech"] });
      }
      if (action === "reject") onRejected();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const next = NEXT[job.status] || [];
  if (!next.length) return null;
  return (
    <div className="card-soft p-4 space-y-3" data-testid="tech-actions">
      <p className="font-semibold text-sm">Next step</p>
      {job.status === "accepted" && (
        <div className="flex flex-wrap gap-2 items-center text-sm" data-testid="tech-eta-options">
          <span className="text-muted-foreground">Reaching in:</span>
          {ETAS.map((m) => <button key={m} type="button" onClick={() => setEta(m)} className={`rounded-full px-3 py-1 border text-xs font-semibold ${eta === m ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`tech-eta-${m}`}>{m} min</button>)}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {next.map(([a, l, I]) => (
          <Button key={a} className="rounded-full h-11 px-6 flex-1 sm:flex-none" disabled={!!busy} onClick={() => run(a, a === "start_journey" ? { eta_minutes: eta } : {})} data-testid={`tech-action-${a}`}>
            {busy === a ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <I className="h-4 w-4 mr-2" />}{l}
          </Button>
        ))}
        {["assigned", "accepted"].includes(job.status) && <Button variant="outline" className="rounded-full h-11 px-6 border-destructive/40 text-destructive" onClick={() => setRej(true)} data-testid="tech-action-reject"><X className="h-4 w-4 mr-2" />Reject</Button>}
      </div>
      <Dialog open={rej} onOpenChange={setRej}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="tech-reject-dialog">
          <DialogHeader><DialogTitle>Reject {job.booking_no}?</DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. not available at this time, too far, need different skill)" data-testid="tech-reject-reason" />
          <Button variant="destructive" disabled={reason.trim().length < 3 || !!busy} onClick={() => run("reject", { reason })} data-testid="tech-reject-confirm">Reject job</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
